import { Support } from '@ixigo-packages/support';
import { createFileRoute } from '@tanstack/react-router';
import {
	type ApiErrorResponse,
	type BookingSuccessResponse,
	getBookingDetail,
	type TripErrorType,
} from '@/api';
import { NavigationHeader } from '@/components';
import { useNavigate } from '@/lib/navigation';
import { ErrorView } from '../-components/error-view';
import BookingContent from './-components/booking-content';
import { ConfirmationHeader } from './-components/header';

interface SupportSearchParams {
	bookingId: string;
}

function SupportPage() {
	const bookingData = Route.useLoaderData();
	const { bookingId } = Route.useSearch();
	const navigate = useNavigate();
	const { bookingStatus } = bookingData.data.bookingDetail;
	return (
		<div className="min-h-screen bg-tertiary main-content">
			<NavigationHeader
				title="Customer Service"
				leftAction={() => {
					navigate({
						to: '/trips/$bookingId',
						params: { bookingId },
						back: true,
						replace: true,
					});
				}}
				className="sticky top-0 z-[1] w-full px-20 flex-nowrap border-b border-tertiary bg-primary"
			/>
			<div className="flex flex-col flex-1 gap-y-10">
				<div className="bg-primary flex flex-col">
					<ConfirmationHeader
						bookingId={bookingId}
						bookingStatus={bookingStatus}
					/>
					<BookingContent bookingData={bookingData.data} />
				</div>
				<Support bookingID={bookingId} />
			</div>
		</div>
	);
}

class TripError extends Error {
	public readonly errorType: TripErrorType;
	public readonly errors: ApiErrorResponse<TripErrorType>['errors'];

	constructor(
		errorType: TripErrorType,
		errors: ApiErrorResponse<TripErrorType>['errors'],
	) {
		super();
		this.errorType = errorType;
		this.errors = errors;

		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, TripError);
		}
	}
}

async function supportLoader(context: {
	location: { search: Record<string, unknown> };
}): Promise<BookingSuccessResponse> {
	const bookingId = context.location.search.bookingId;

	if (!bookingId || typeof bookingId !== 'string') {
		throw new TripError('INVALID_BOOKING_ID', {
			type: 'INVALID_BOOKING_ID',
			title: 'Invalid booking ID',
			message: "The page you're looking for does not exist.",
		});
	}

	const response = await getBookingDetail(bookingId);
	if ('errors' in response) {
		const errorType = response.errors.type || 'API_ERROR';
		throw new TripError(errorType, response.errors);
	}
	return response;
}

function isTripError(error: unknown): error is TripError {
	return (
		typeof error === 'object' &&
		error !== null &&
		'errorType' in error &&
		'errors' in error
	);
}

export const Route = createFileRoute('/_auth/support/')({
	validateSearch: (search: Record<string, unknown>): SupportSearchParams => {
		const bookingId = search.bookingId;
		if (!bookingId || typeof bookingId !== 'string') {
			throw new Error('bookingId is required');
		}

		return {
			bookingId,
		};
	},
	component: SupportPage,
	loader: supportLoader,
	ssr: false,
	errorComponent: ({ error }) => {
		if (isTripError(error)) {
			return (
				<ErrorView
					title={error.errors.title}
					description={error.errors.message}
					errorType={error.errorType}
					fullScreen
				/>
			);
		}
		return (
			<ErrorView
				title=""
				description={error.message}
				errorType="GENERIC_ERROR"
				fullScreen
			/>
		);
	},
});
